import { singleton, container } from "tsyringe";
import { html } from "lit";
import { translate } from "lit-translate";
import { UiHelper } from "../helpers/ui.helper";
import { NOTIFICATION_DURATION } from "../constants";
import { BaseMessage } from "../message-broker/base-message.interface";
import { EntityCreatedPayload } from "../message-broker/payloads/entity-created.payload";
import { EntityChangedPayload } from "../message-broker/payloads/entity-changed.payload";
import { EntityDeletedPayload } from "../message-broker/payloads/entity-deleted.payload";
import { Entity } from "../domain/entities/entity";
import { Issue } from "../domain/entities/issue.entity";
import { Project } from "../domain/entities/project.entity";
import { Employee } from "../domain/entities/employee.entity";
import { IssueStatus } from "../domain/entities/issue-status.entity";
import { IssueType } from "../domain/entities/issue-type.entity";
import { Keyword } from "../domain/entities/keyword.entity";
import { ProjectType } from "../domain/entities/project-type.entity";
import { EmployeeToIssueAssignment } from "../domain/entities/employee-to-issue-assignment.entity";
import { AbstractBaseListener } from "../message-broker/abstract-base.listener";
import { OnMessage } from "../message-broker/on-message.decorator";
import { EntityManager } from "../persistence/entity.manager";
import { ArrayHelper } from "../helpers/array.helper";
import { entityDisplayName } from "./entity-display-name";

/**
 * Global notification service for entity operations.
 * 
 * This service listens to all entity lifecycle events (created, changed, deleted)
 * via the MessageBroker and displays appropriate toast notifications using UiHelper.
 * 
 * **Benefits:**
 * - Centralized notification logic (DRY principle)
 * - Consistent toast messages across the app
 * - Automatic notifications for all entity operations
 * - No manual notification code needed in dialogs/components
 * 
 * **Architecture:**
 * - Extends AbstractBaseListener for MessageBroker integration
 * - Uses @OnMessage decorators for event handling
 * - Singleton service - one instance for the entire app
 */
@singleton()
export class GlobalNotificationService extends AbstractBaseListener {

    /**
     * Configuration for notification behavior
     */
    private readonly config = {
        // Entity types that should show notifications
        enabledEntityTypes: [
            'Issue',
            'Project', 
            'Employee',
            'IssueStatus',
            'IssueType',
            'Keyword',
            'ProjectType'
        ],
        
        // Default notification duration
        defaultDuration: NOTIFICATION_DURATION,
        
        // Show notifications for batch operations
        showBatchNotifications: true,
        
        // Maximum entities to mention by name in batch notifications
        maxEntityNamesInBatch: 3
    };

    /**
     * Entity-specific notification templates.
     * Each entity type has custom messages for different operations.
     */
    private readonly notificationTemplates = {
        Issue: {
            entityName: 'Task',
            created: {
                title: 'Task erstellt',
                message: (name: string) => `Task "${name}" wurde erfolgreich erstellt.`
            },
            changed: {
                title: 'Task gespeichert', 
                message: (name: string) => `Task "${name}" wurde erfolgreich aktualisiert.`
            },
            deleted: {
                title: 'Task gelöscht',
                message: (name: string) => `Task "${name}" wurde erfolgreich gelöscht.`
            },
            batchCreated: {
                title: (count: number) => `${count} Tasks erstellt`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich erstellt.`
            },
            batchChanged: {
                title: (count: number) => `${count} Tasks gespeichert`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich aktualisiert.`
            },
            batchDeleted: {
                title: (count: number) => `${count} Tasks gelöscht`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich gelöscht.`
            }
        },
        Project: {
            entityName: 'Projekt',
            created: {
                title: 'Projekt erstellt',
                message: (name: string) => `Projekt "${name}" wurde erfolgreich angelegt.`
            },
            changed: {
                title: 'Projekt aktualisiert',
                message: (name: string) => `Projekt "${name}" wurde erfolgreich bearbeitet.`
            },
            deleted: {
                title: 'Projekt gelöscht',
                message: (name: string) => `Projekt "${name}" wurde erfolgreich entfernt.`
            },
            batchCreated: {
                title: (count: number) => `${count} Projekte erstellt`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich angelegt.`
            },
            batchChanged: {
                title: (count: number) => `${count} Projekte aktualisiert`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich bearbeitet.`
            },
            batchDeleted: {
                title: (count: number) => `${count} Projekte gelöscht`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich entfernt.`
            }
        },
        Employee: {
            entityName: 'Mitarbeiter',
            created: {
                title: 'Mitarbeiter angelegt',
                message: (name: string) => `Mitarbeiter "${name}" wurde erfolgreich hinzugefügt.`
            },
            changed: {
                title: 'Mitarbeiter aktualisiert',
                message: (name: string) => `Mitarbeiter "${name}" wurde erfolgreich bearbeitet.`
            },
            deleted: {
                title: 'Mitarbeiter entfernt',
                message: (name: string) => `Mitarbeiter "${name}" wurde erfolgreich entfernt.`
            },
            batchCreated: {
                title: (count: number) => `${count} Mitarbeiter angelegt`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich hinzugefügt.`
            },
            batchChanged: {
                title: (count: number) => `${count} Mitarbeiter aktualisiert`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich bearbeitet.`
            },
            batchDeleted: {
                title: (count: number) => `${count} Mitarbeiter entfernt`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich entfernt.`
            }
        },
        IssueStatus: {
            entityName: 'Status',
            created: {
                title: 'Status erstellt',
                message: (name: string) => `Status "${name}" wurde erfolgreich angelegt.`
            },
            changed: {
                title: 'Status aktualisiert',
                message: (name: string) => `Status "${name}" wurde erfolgreich geändert.`
            },
            deleted: {
                title: 'Status gelöscht',
                message: (name: string) => `Status "${name}" wurde erfolgreich entfernt.`
            },
            batchCreated: {
                title: (count: number) => `${count} Status erstellt`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich angelegt.`
            },
            batchChanged: {
                title: (count: number) => `${count} Status aktualisiert`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich geändert.`
            },
            batchDeleted: {
                title: (count: number) => `${count} Status gelöscht`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich entfernt.`
            }
        },
        IssueType: {
            entityName: 'Task-Typ',
            created: {
                title: 'Task-Typ erstellt',
                message: (name: string) => `Task-Typ "${name}" wurde erfolgreich angelegt.`
            },
            changed: {
                title: 'Task-Typ aktualisiert',
                message: (name: string) => `Task-Typ "${name}" wurde erfolgreich geändert.`
            },
            deleted: {
                title: 'Task-Typ gelöscht',
                message: (name: string) => `Task-Typ "${name}" wurde erfolgreich entfernt.`
            },
            batchCreated: {
                title: (count: number) => `${count} Task-Typen erstellt`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich angelegt.`
            },
            batchChanged: {
                title: (count: number) => `${count} Task-Typen aktualisiert`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich geändert.`
            },
            batchDeleted: {
                title: (count: number) => `${count} Task-Typen gelöscht`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich entfernt.`
            }
        },
        Keyword: {
            entityName: 'Schlagwort',
            created: {
                title: 'Schlagwort erstellt',
                message: (name: string) => `Schlagwort "${name}" wurde erfolgreich angelegt.`
            },
            changed: {
                title: 'Schlagwort aktualisiert',
                message: (name: string) => `Schlagwort "${name}" wurde erfolgreich geändert.`
            },
            deleted: {
                title: 'Schlagwort gelöscht',
                message: (name: string) => `Schlagwort "${name}" wurde erfolgreich entfernt.`
            },
            batchCreated: {
                title: (count: number) => `${count} Schlagwörter erstellt`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich angelegt.`
            },
            batchChanged: {
                title: (count: number) => `${count} Schlagwörter aktualisiert`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich geändert.`
            },
            batchDeleted: {
                title: (count: number) => `${count} Schlagwörter gelöscht`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich entfernt.`
            }
        },
        ProjectType: {
            entityName: 'Projekt-Typ',
            created: {
                title: 'Projekt-Typ erstellt',
                message: (name: string) => `Projekt-Typ "${name}" wurde erfolgreich angelegt.`
            },
            changed: {
                title: 'Projekt-Typ aktualisiert',
                message: (name: string) => `Projekt-Typ "${name}" wurde erfolgreich geändert.`
            },
            deleted: {
                title: 'Projekt-Typ gelöscht',
                message: (name: string) => `Projekt-Typ "${name}" wurde erfolgreich entfernt.`
            },
            batchCreated: {
                title: (count: number) => `${count} Projekt-Typen erstellt`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich angelegt.`
            },
            batchChanged: {
                title: (count: number) => `${count} Projekt-Typen aktualisiert`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich geändert.`
            },
            batchDeleted: {
                title: (count: number) => `${count} Projekt-Typen gelöscht`,
                message: (entities: string, count: number) => `${entities} wurden erfolgreich entfernt.`
            }
        }
    };

    /**
     * Surfaces an ad-hoc error notification to the user. Use this when a
     * component-level operation fails (e.g. a save click on a whiteboard) and
     * the user needs to be informed without the failure being tied to an
     * entity lifecycle event. The optional `message` adds detail below the
     * title; both are rendered via `UiHelper.notify` at level `warning`.
     */
    public error(title: string, message?: string): void {
        UiHelper.notify({
            closable: true,
            title,
            level: 'warning',
            content: message ? html`<p>${message}</p>` : html``,
            duration: this.config.defaultDuration,
        });
    }

    /**
     * Handles entity creation events.
     * Shows success notifications when entities are successfully created.
     *
     * @param messages Array of entity created messages
     */
    @OnMessage('entity.created', (message: BaseMessage<EntityCreatedPayload<Entity>>) => true)
    private onEntityCreated(messages: Array<BaseMessage<EntityCreatedPayload<Entity>>>): void {
        for (const message of messages) {
            const entity = message.payload.entity;
            const entityType = this.getEntityTypeName(entity);
            
            // Only show notifications for configured entity types
            if (!this.config.enabledEntityTypes.includes(entityType)) {
                continue;
            }

            const notification = this.getNotificationTemplate(entityType, 'created', entity);
            if (!notification) {
                continue; // Skip if no template available
            }
            
            UiHelper.notify({
                closable: true,
                title: notification.title,
                level: 'success',
                content: html`<p>${notification.message}</p>`,
                duration: this.config.defaultDuration
            });
        }
    }

    /**
     * Handles entity change events.
     * Shows info notifications when entities are successfully updated.
     * 
     * Note: EntityChangedPayload has different structure - it contains uid, prototype, changeSet
     * but no entity. We need to load entities via EntityManager.
     * 
     * @param messages Array of entity changed messages
     */
    @OnMessage('entity.changed', (message: BaseMessage<EntityChangedPayload<Entity>>) => true)
    private async onEntityChanged(messages: Array<BaseMessage<EntityChangedPayload<Entity>>>): Promise<void> {
        // Remove duplicates by uid
        messages = ArrayHelper.unique(messages, m => m.payload.uid);
        
        // Group messages by entity type for batch handling
        const messagesByType = this.groupChangedMessagesByEntityType(messages);
        
        for (const [entityType, entityMessages] of messagesByType) {
            // Only show notifications for configured entity types
            if (!this.config.enabledEntityTypes.includes(entityType)) {
                continue;
            }

            // Load entities from repository
            const entities = await this.loadEntitiesFromChangedMessages(entityMessages);
            if (entities.length === 0) continue;

            if (entities.length === 1) {
                // Single entity update
                const entity = entities[0];
                const notification = this.getNotificationTemplate(entityType, 'changed', entity);
                if (!notification) continue;

                UiHelper.notify({
                    closable: true,
                    title: notification.title,
                    level: 'info',
                    content: html`<p>${notification.message}</p>`,
                    duration: this.config.defaultDuration
                });
            } else if (this.config.showBatchNotifications) {
                // Batch update
                const notification = this.getBatchNotificationTemplate(
                    entityType,
                    'changed',
                    entities
                );
                if (!notification) continue;

                UiHelper.notify({
                    closable: true,
                    title: notification.title,
                    level: 'info',
                    content: html`<p>${notification.message}</p>`,
                    duration: this.config.defaultDuration
                });
            }
        }
    }

    /**
     * Handles entity deletion events.
     * Shows warning notifications when entities are successfully deleted.
     * 
     * @param messages Array of entity deleted messages
     */
    @OnMessage('entity.deleted', (message: BaseMessage<EntityDeletedPayload<Entity>>) => true)
    private onEntityDeleted(messages: Array<BaseMessage<EntityDeletedPayload<Entity>>>): void {
        // Group messages by entity type for batch handling
        const messagesByType = this.groupMessagesByEntityType(
            messages.map(m => ({ ...m, payload: { ...m.payload, entity: m.payload.entity } }))
        );
        
        for (const [entityType, entityMessages] of messagesByType) {
            // Only show notifications for configured entity types
            if (!this.config.enabledEntityTypes.includes(entityType)) {
                continue;
            }

            if (entityMessages.length === 1) {
                // Single entity deletion
                const entity = entityMessages[0].payload.entity;
                const notification = this.getNotificationTemplate(entityType, 'deleted', entity);
                if (!notification) continue;

                UiHelper.notify({
                    closable: true,
                    title: notification.title,
                    level: 'warning',
                    content: html`<p>${notification.message}</p>`,
                    duration: this.config.defaultDuration
                });
            } else if (this.config.showBatchNotifications) {
                // Batch deletion
                const notification = this.getBatchNotificationTemplate(
                    entityType,
                    'deleted',
                    entityMessages.map(m => m.payload.entity)
                );
                if (!notification) continue;

                UiHelper.notify({
                    closable: true,
                    title: notification.title,
                    level: 'warning',
                    content: html`<p>${notification.message}</p>`,
                    duration: this.config.defaultDuration
                });
            }
        }
    }

    /**
     * Gets the appropriate notification template for a single entity operation.
     * 
     * @param entityType The type of entity (e.g., 'Issue', 'Project')
     * @param operation The operation type ('created', 'changed', 'deleted')
     * @param entity The entity instance
     * @returns Notification template with title and message, or null if no template available
     */
    private getNotificationTemplate(
        entityType: string,
        operation: 'created' | 'changed' | 'deleted',
        entity: Entity
    ): { title: string; message: string } | null {
        const template = this.notificationTemplates[entityType as keyof typeof this.notificationTemplates];
        if (!template) {
            return null; // No template available for this entity type
        }

        const displayName = entityDisplayName(entity);
        const operationTemplate = template[operation];
        
        return {
            title: operationTemplate.title,
            message: operationTemplate.message(displayName)
        };
    }

    /**
     * Gets the appropriate notification template for batch operations.
     * 
     * @param entityType The type of entities
     * @param operation The operation type
     * @param entities Array of entities involved in the operation
     * @returns Notification template for batch operation, or null if no template available
     */
    private getBatchNotificationTemplate(
        entityType: string,
        operation: 'created' | 'changed' | 'deleted',
        entities: Entity[]
    ): { title: string; message: string } | null {
        const template = this.notificationTemplates[entityType as keyof typeof this.notificationTemplates];
        if (!template) {
            return null; // No template available for this entity type
        }

        const count = entities.length;
        
        // Show first few entity names, then "and X more"
        const displayNames = entities
            .slice(0, this.config.maxEntityNamesInBatch)
            .map(e => entityDisplayName(e));
        
        const remainingCount = Math.max(0, count - this.config.maxEntityNamesInBatch);
        
        let entityList = displayNames.join(', ');
        if (remainingCount > 0) {
            entityList += ` und ${remainingCount} weitere`;
        }

        const batchOperationKey = `batch${operation.charAt(0).toUpperCase() + operation.slice(1)}` as 
            'batchCreated' | 'batchChanged' | 'batchDeleted';
        
        const batchTemplate = template[batchOperationKey];
        
        return {
            title: batchTemplate.title(count),
            message: batchTemplate.message(entityList, count)
        };
    }

    /**
     * Groups messages by entity type for batch handling.
     * 
     * @param messages Array of messages to group
     * @returns Map of entity type to messages
     */
    private groupMessagesByEntityType(messages: any[]): Map<string, any[]> {
        const groups = new Map<string, any[]>();
        
        for (const message of messages) {
            const entityType = this.getEntityTypeName(message.payload.entity);
            
            if (!groups.has(entityType)) {
                groups.set(entityType, []);
            }
            groups.get(entityType)!.push(message);
        }
        
        return groups;
    }

    /**
     * Groups entity.changed messages by entity type.
     * For EntityChangedPayload, the entity type is in prototype property.
     * 
     * @param messages Array of EntityChangedPayload messages
     * @returns Map of entity type to messages
     */
    private groupChangedMessagesByEntityType(messages: Array<BaseMessage<EntityChangedPayload<Entity>>>): Map<string, Array<BaseMessage<EntityChangedPayload<Entity>>>> {
        const groups = new Map<string, Array<BaseMessage<EntityChangedPayload<Entity>>>>();
        
        for (const message of messages) {
            const entityType = message.payload.prototype.name;
            
            if (!groups.has(entityType)) {
                groups.set(entityType, []);
            }
            groups.get(entityType)!.push(message);
        }
        
        return groups;
    }

    /**
     * Loads entities from EntityChangedPayload messages using EntityManager.
     * 
     * @param messages Array of entity changed messages
     * @returns Array of loaded entities
     */
    private async loadEntitiesFromChangedMessages(messages: Array<BaseMessage<EntityChangedPayload<Entity>>>): Promise<Entity[]> {
        const entities: Entity[] = [];
        
        for (const message of messages) {
            try {
                const repository = container
                    .resolve(EntityManager)
                    .getRepositoryForEntity(message.payload.prototype);
                
                if (repository) {
                    const entity = await repository.findOneById(message.payload.uid);
                    if (entity) {
                        entities.push(entity);
                    }
                }
            } catch (error) {
                console.warn(`[GlobalNotificationService] Failed to load entity ${message.payload.uid}:`, error);
            }
        }
        
        return entities;
    }

    /**
     * Gets the type name of an entity.
     * 
     * @param entity The entity to get type name for
     * @returns String representation of entity type
     */
    private getEntityTypeName(entity: Entity): string {
        return entity.constructor.name;
    }



    /**
     * Configures notification behavior.
     * Can be called to customize which entities show notifications.
     * 
     * @param config Partial configuration to merge
     */
    public configure(config: Partial<typeof this.config>): void {
        Object.assign(this.config, config);
    }

    /**
     * Adds or updates notification templates for an entity type.
     * 
     * @param entityType The entity type name (e.g., 'CustomEntity')
     * @param templates The notification templates for this entity type
     */
    public addNotificationTemplates(
        entityType: string,
        templates: typeof this.notificationTemplates.Issue
    ): void {
        (this.notificationTemplates as any)[entityType] = templates;
        
        // Also enable notifications for this entity type if not already enabled
        if (!this.config.enabledEntityTypes.includes(entityType)) {
            this.config.enabledEntityTypes.push(entityType);
        }
    }

    /**
     * Enables notifications for specific entity types.
     * 
     * @param entityTypes Array of entity type names to enable
     */
    public enableNotificationsFor(entityTypes: string[]): void {
        this.config.enabledEntityTypes = [
            ...new Set([...this.config.enabledEntityTypes, ...entityTypes])
        ];
    }

    /**
     * Disables notifications for specific entity types.
     * 
     * @param entityTypes Array of entity type names to disable
     */
    public disableNotificationsFor(entityTypes: string[]): void {
        this.config.enabledEntityTypes = this.config.enabledEntityTypes
            .filter(type => !entityTypes.includes(type));
    }
}